import {
    ActivityModality,
    AUDIENCE_TYPE,
    CatalogItem,
    ClassroomAssignments,
    CourseCatalogItemLimited,
    CourseProps,
    Evaluation,
    EvaluationStatus,
    LearningActivitiesByUserIdResponse,
    LearningActivity,
    LmsType,
    PARTNER_PUBLIC_UNLISTED,
    Registration,
    RegistrationStatus,
    RegistrationType,
    Schedule,
    SEMI_PRIVATE_AUDIENCE,
    Transcript,
    VIRTUAL_ILT_LOCATION,
    WaitlistEnabled,
} from './types';
import { CourseDetailsMapper } from '../mapper';
import { loggerFactory } from '@amzn/aws-tc-xb-xps';
import { CATALOG_ITEM_NOT_FOUND_ERROR, INVALID_COURSE_DETAILS_ERROR } from './rumMetrics';

const logger = loggerFactory({
    componentName: 'courseBuilder',
});

function formatLocation(learningActivity: LearningActivity): string {
    if (learningActivity.activity_modality === ActivityModality.vILT) {
        return VIRTUAL_ILT_LOCATION;
    }

    const addressParts = [
        learningActivity.delivery_address_1,
        learningActivity.delivery_address_2,
        learningActivity.room,
        learningActivity.delivery_city,
        learningActivity.delivery_state,
        learningActivity.delivery_postal_code,
        learningActivity.delivery_country,
    ];

    return addressParts.filter(Boolean).join(', ');
}

const isSemiPrivate = (learningActivity: LearningActivity | undefined) => {
    return learningActivity?.activity_audience && SEMI_PRIVATE_AUDIENCE.includes(learningActivity.activity_audience as AUDIENCE_TYPE);
};

const zeroIfNegative = (counter: number) => {
    return counter < 0 ? 0 : counter;
};

const calculateSeatsRemaining = (learningActivity: LearningActivity, orgId: string | undefined) => {
    const seatsPerOrg = learningActivity.customers
        ?.filter((customer) => customer.organization?.id === orgId)
        ?.map((customer) => customer?.number_of_students_committed ?? 0)
        // Take sum of all the number_of_students_committed
        .reduce((sum, count) => sum + count, 0);

    const registrationCounterPerOrg = learningActivity.registrationCounterPerOrganization?.find(
        (organization) => organization.organization.id === orgId,
    )?.registrationCounter;
    const globalSeatsLeft = (learningActivity.class_size ?? 0) - (learningActivity.registrationCounter ?? 0);

    if (isSemiPrivate(learningActivity)) {
        if (registrationCounterPerOrg === undefined || seatsPerOrg === undefined) {
            return zeroIfNegative(globalSeatsLeft);
        } else {
            return zeroIfNegative(seatsPerOrg - registrationCounterPerOrg);
        }
    } else {
        return zeroIfNegative(globalSeatsLeft);
    }
};

const isPartnerPublicUnlistedClass = (learningActivity: LearningActivity | undefined): boolean => {
    return !!(learningActivity?.activity_audience && PARTNER_PUBLIC_UNLISTED.includes(learningActivity.activity_audience as AUDIENCE_TYPE));
};

const extractTeamIds = (learningActivity: LearningActivity | undefined): string[] => {
    type customerWithTeam = {
        team: {
            id: string;
        };
    };
    return (
        learningActivity?.customers
            ?.filter((customer): customer is customerWithTeam => !!customer.team)
            .map((customer) => customer.team.id) ?? []
    );
};

export const convertLearningActivityToCourse = (
    learningActivity: LearningActivity,
    orgId: string | undefined,
): Omit<CourseProps, 'isProxy'> | undefined => {
    const catalogItem: CatalogItem | undefined = learningActivity.course_catalog_item;
    const catalogItemLimited: CourseCatalogItemLimited | undefined = learningActivity.course_catalog_item_limited;

    if (!catalogItem && !catalogItemLimited) {
        const error: Error = new Error(`No catalog item found for learning activity ${learningActivity.pk}`);
        logger.error(error);
        logger.count(CATALOG_ITEM_NOT_FOUND_ERROR);
        return;
    }

    const useLimitedCatalogItem = !catalogItem && !!catalogItemLimited && learningActivity.program === 'Dedicated Cloud';

    // if catalogItem is null and we can't use the limited item
    // (e.g., program is not Dedicated Cloud), return early to prevent runtime crash
    if (!catalogItem && !useLimitedCatalogItem) {
        const error: Error = new Error(`No catalog item found for learning activity ${learningActivity.pk}`);
        logger.error(error);
        logger.count(CATALOG_ITEM_NOT_FOUND_ERROR);
        return;
    }

    const schedule: Schedule = {
        classDays: learningActivity.delivery_sessions.map((deliverySession) => ({
            // TypeScript expects milliseconds, but the Grimsby timestamp is in seconds
            startDateTime: new Date(deliverySession.start_timestamp * 1_000),
            endDateTime: new Date(deliverySession.end_timestamp * 1_000),
        })),
        language: learningActivity.delivery_language,
        timezone: learningActivity.delivery_timezone,
        seatsRemaining: calculateSeatsRemaining(learningActivity, orgId),
    };

    // Extract commercialPrivateCourseOrgIds from all customers
    const commercialPrivateCourseOrgIds =
        learningActivity.customers && learningActivity.customers.length > 0
            ? learningActivity.customers.map((customer) => customer.organization?.id).filter((id): id is string => !!id)
            : undefined;

    return {
        id: learningActivity.pk,
        courseStatus: learningActivity.activity_status,
        classroomUrl: learningActivity.classrooms_student_url,
        meetingLink: learningActivity.v_ilt_id,
        schedule,
        courseDetails: useLimitedCatalogItem
            ? CourseDetailsMapper.fromCatalogItemLimited(catalogItemLimited!)
            : CourseDetailsMapper.fromCatalogItem(mergeLocalizedCatalogItem(catalogItem!, learningActivity.localized_course_catalog_item)),
        activityName: learningActivity.activity_name,
        activityModality: learningActivity.activity_modality,
        location: formatLocation(learningActivity),
        waitlistEnabled: learningActivity.waitlist_enabled || WaitlistEnabled.DISABLED,
        commercialPrivateCourseOrgIds,
        isPartnerPublicUnlistedClass: isPartnerPublicUnlistedClass(learningActivity),
        studentNotes: learningActivity.student_notes?.description,
        preWorkInstruction: learningActivity.pre_work_instruction?.description,
        teamIds: extractTeamIds(learningActivity),
        vIltType: learningActivity.v_ilt_type,
        program: learningActivity.program,
        learningGoalIds: learningActivity.learning_goal_ids,
        contentConfigs: learningActivity.content_configs,
    };
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const convertLearningActivitiesToCourses = (
    learningActivitiesResult: LearningActivitiesByUserIdResponse,
    orgId: string | undefined,
    // Activities from the learner's assigned learning goals (TSA). Fetched by a separate query
    // (useLearningGoalTranscriptsByUserId) and passed in so its failures stay isolated from the
    // main learner-records request. Same LearningActivity shape, so they merge into the same list
    // and the pk-based dedup below drops any already surfaced through registrations/assignments.
    learningGoalActivities: LearningActivity[] = [],
): CourseProps[] => {
    const registrations: Registration[] = learningActivitiesResult.registrationsByUserId.nodes;
    const transcripts: Transcript[] = learningActivitiesResult.transcriptsByUserId.nodes;
    const evaluations: Evaluation[] = learningActivitiesResult.iltEvaluationsByUserId.nodes;

    const registeredLearningActivities = registrations.map((registration) => registration.learningActivity);

    let aggregatedLearningActivities: LearningActivity[] = registeredLearningActivities;
    const partnerPublicUnlistedActivities: LearningActivity[] = learningActivitiesResult.learningActivities;
    aggregatedLearningActivities.push(...partnerPublicUnlistedActivities);

    const assignments: ClassroomAssignments[] = learningActivitiesResult.classroomTrainingAssignmentsByUserId.nodes;
    const assignedLearningActivities = assignments.map((assignment) => assignment.learningActivity);
    aggregatedLearningActivities = aggregatedLearningActivities.concat(assignedLearningActivities);

    aggregatedLearningActivities = aggregatedLearningActivities.concat(learningGoalActivities);

    return aggregatedLearningActivities
        .reduce((acc: LearningActivity[], current: LearningActivity) => {
            if (!acc.find((item: LearningActivity) => item.pk === current.pk)) {
                acc.push(current);
            }
            return acc;
        }, [])
        .filter((learningActivity) => learningActivity.lms_type === LmsType.myClass)
        .flatMap((learningActivity) => {
            const registration = registrations.find((reg) => reg.learningActivity.pk === learningActivity.pk);
            const registrationStatus = registration
                ? registration.registrationStatus === RegistrationStatus.UNWAITLISTED ||
                  registration.registrationStatus === RegistrationStatus.WITHDRAWN
                    ? RegistrationStatus.WITHDRAWN
                    : registration.registrationStatus
                : RegistrationStatus.WITHDRAWN;
            const isProxy = registration?.registrationType === RegistrationType.PROXY;
            const transcript = transcripts.find((transcript) => transcript.learningActivity.pk === learningActivity.pk);
            const evaluation = evaluations.find((evaluation) => evaluation.learningActivity.pk === learningActivity.pk);

            const courseProps: Omit<CourseProps, 'isProxy'> | undefined = convertLearningActivityToCourse(learningActivity, orgId);
            if (!courseProps) {
                const error: Error = new Error(`Unable to get course details from learning activity ${learningActivity.pk}`);
                logger.error(error);
                logger.count(INVALID_COURSE_DETAILS_ERROR);
                return [];
            }

            return [
                {
                    ...courseProps,
                    registrationStatus: registrationStatus,
                    transcriptStatus: transcript?.transcriptStatus,
                    transcriptId: transcript?.id,
                    evaluationStatus: evaluation?.evaluationStatus ?? EvaluationStatus.NOT_REQUIRED,
                    evaluationUrl: evaluation?.evaluationUrl,
                    isProxy,
                },
            ];
        });
};

/**
 * Take every field from the delivery-language variant, falling back to the English base wherever the
 * variant has no value. Empty strings and empty lists count as no value, since the mapper would
 * otherwise render a blank where English text was available.
 */
export const mergeLocalizedCatalogItem = (base: CatalogItem, localized?: CatalogItem): CatalogItem => {
    if (!localized) return base;

    return {
        ...base,
        id: localized.id || base.id,
        versionedId: localized.versionedId || base.versionedId,
        name: localized.name || base.name,
        description: localized.description || base.description,
        duration: localized.duration ?? base.duration,
        domain: localized.domain?.valueDisplayString ? localized.domain : base.domain,
        skillLevel: localized.skillLevel?.valueDisplayString ? localized.skillLevel : base.skillLevel,
        objectives: localized.objectives?.values?.length ? localized.objectives : base.objectives,
        intendedAudience: localized.intendedAudience?.values?.length ? localized.intendedAudience : base.intendedAudience,
        prerequisites: localized.prerequisites?.value ? localized.prerequisites : base.prerequisites,
        outline: localized.outline || base.outline,
        children: localized.children?.nodes?.length ? localized.children : base.children,
    };
};

export const testExport = { calculateSeatsRemaining, extractTeamIds };
